const APP_URL = import.meta.env.VITE_APP_URL || 'https://app.fastenai.dev';

// Import auth functions for token refresh
import { refreshAccessToken, triggerReAuthentication } from './onshape-auth';

/**
 * Get stored access token from sessionStorage
 * @returns {string|null} Access token or null if not found
 */
function getStoredAccessToken() {
  try {
    const token = sessionStorage.getItem('fastenai_access_token');
    return token;
  } catch (error) {
    console.error('[onshape-api] Error reading access token from storage:', error);
    return null;
  }
}

/**
 * Handle token expiration by attempting refresh or re-authentication
 * @returns {Promise<string>} New access token
 * @throws {Error} If token refresh/re-auth fails
 */
async function handleTokenExpiration() {
  try {
    // First, try to refresh using refresh_token
    const refreshResult = await refreshAccessToken();
    if (refreshResult.success && refreshResult.access_token) {
      return refreshResult.access_token;
    }
  } catch (refreshError) {
    console.warn('[onshape-api] Token refresh failed:', refreshError.message);
  }
  
  // If refresh failed or no refresh_token available, trigger re-authentication
  try {
    const authResult = await triggerReAuthentication();
    if (authResult.success && authResult.access_token) {
      return authResult.access_token;
    }
    throw new Error('Re-authentication did not return a valid token');
  } catch (authError) {
    console.error('[onshape-api] Re-authentication failed:', authError);
    throw new Error(`Failed to refresh or re-authenticate: ${authError.message}`);
  }
}

/**
 * Get part studio features from Onshape API via backend
 * Automatically handles token expiration by refreshing or re-authenticating
 * @param {string} documentId - Onshape document ID
 * @param {string} workspaceId - Onshape workspace ID
 * @param {string} elementId - Onshape element ID (part studio)
 * @param {number} retryCount - Internal retry counter (default: 0)
 * @returns {Promise<Object>} Response with features list
 */
export async function getFeatures(documentId, workspaceId, elementId, retryCount = 0) {
  // Validate required parameters
  if (!documentId || !workspaceId || !elementId) {
    throw new Error('Missing required parameters: documentId, workspaceId, elementId');
  }

  // Prevent infinite retry loops
  const MAX_RETRIES = 1;
  if (retryCount > MAX_RETRIES) {
    throw new Error('Maximum retry attempts reached. Please try again later.');
  }

  // Build query string
  const params = new URLSearchParams({
    documentId,
    workspaceId,
    elementId,
  });

  const url = `${APP_URL}/api/get-features?${params.toString()}`;

  // Get access token from storage for Authorization header
  let accessToken = getStoredAccessToken();
  const headers = {
    'Content-Type': 'application/json',
  };
  
  // Add Authorization header if token is available
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers,
      credentials: 'include', // Still include cookies as fallback
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ 
        error: `HTTP error! status: ${response.status}` 
      }));
      
      // Handle 401 Unauthorized - token expired
      if (response.status === 401) {
        // Only retry if we haven't already retried
        if (retryCount === 0) {
          try {
            // Attempt to refresh/re-authenticate
            const newAccessToken = await handleTokenExpiration();
            
            // Retry the request with new token
            return getFeatures(documentId, workspaceId, elementId, retryCount + 1);
          } catch (refreshError) {
            // If refresh/re-auth failed, throw the original 401 error with helpful message
            throw new Error('Unauthorized: Invalid or expired access token. Please sign in again.');
          }
        } else {
          // Already retried once, throw error
          throw new Error('Unauthorized: Invalid or expired access token. Please sign in again.');
        }
      }
      
      // For other errors, throw normally
      throw new Error(errorData.error || `Failed to fetch features: ${response.status}`);
    }

    const result = await response.json();

    if (!result.success) {
      throw new Error(result.error || 'Failed to fetch features');
    }

    return result.features;
  } catch (error) {
    // If it's already a handled 401 error, re-throw it
    if (error.message && error.message.includes('Unauthorized')) {
      throw error;
    }
    
    console.error('Error fetching features:', error);
    throw error;
  }
}

/**
 * Get physical context (materials, mass, names) from Onshape API via backend using FeatureScript
 * Automatically handles token expiration by refreshing or re-authenticating
 * @param {string} documentId - Onshape document ID
 * @param {string} workspaceId - Onshape workspace ID
 * @param {string} elementId - Onshape element ID (part studio)
 * @param {number} retryCount - Internal retry counter (default: 0)
 * @returns {Promise<Array>} Response with parts list
 */
export async function getPhysicalContext(documentId, workspaceId, elementId, retryCount = 0) {
  // Validate required parameters
  if (!documentId || !workspaceId || !elementId) {
    throw new Error('Missing required parameters: documentId, workspaceId, elementId');
  }

  // Prevent infinite retry loops
  const MAX_RETRIES = 1;
  if (retryCount > MAX_RETRIES) {
    throw new Error('Maximum retry attempts reached. Please try again later.');
  }

  // Build query string
  const params = new URLSearchParams({
    documentId,
    workspaceId,
    elementId,
  });

  const url = `${APP_URL}/api/get-physical-context?${params.toString()}`;

  // Get access token from storage for Authorization header
  let accessToken = getStoredAccessToken();
  const headers = {
    'Content-Type': 'application/json',
  };
  
  // Add Authorization header if token is available
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers,
      credentials: 'include', // Still include cookies as fallback
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[onshape-api] getPhysicalContext error response:', {
        status: response.status,
        statusText: response.statusText,
        errorText: errorText.substring(0, 500), // First 500 chars
      });
      
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch (e) {
        errorData = { error: `HTTP error! status: ${response.status}`, rawText: errorText };
      }
      
      // Handle 401 Unauthorized - token expired
      if (response.status === 401) {
        // Only retry if we haven't already retried
        if (retryCount === 0) {
          try {
            // Attempt to refresh/re-authenticate
            const newAccessToken = await handleTokenExpiration();
            
            // Retry the request with new token
            return getPhysicalContext(documentId, workspaceId, elementId, retryCount + 1);
          } catch (refreshError) {
            // If refresh/re-auth failed, throw the original 401 error with helpful message
            throw new Error('Unauthorized: Invalid or expired access token. Please sign in again.');
          }
        } else {
          // Already retried once, throw error
          throw new Error('Unauthorized: Invalid or expired access token. Please sign in again.');
        }
      }
      
      // For other errors, throw normally
      throw new Error(errorData.error || `Failed to fetch physical context: ${response.status}`);
    }

    const result = await response.json();

    if (!result.success) {
      console.error('[onshape-api] getPhysicalContext failed:', result.error);
      throw new Error(result.error || 'Failed to fetch physical context');
    }

    return result.parts;
  } catch (error) {
    // If it's already a handled 401 error, re-throw it
    if (error.message && error.message.includes('Unauthorized')) {
      throw error;
    }
    
    console.error('Error fetching physical context:', error);
    throw error;
  }
}

/**
 * Fetches parts metadata (name, description, part number, vendor, etc.) from Onshape.
 * @param {string} documentId - Onshape document ID
 * @param {string} workspaceId - Onshape workspace ID
 * @param {string} elementId - Onshape element ID
 * @param {number} retryCount - Number of retries attempted (for recursion)
 * @returns {Promise<Array>} Array of parts with metadata
 */
export async function getPartsMetadata(documentId, workspaceId, elementId, retryCount = 0) {
  try {
    console.log('[onshape-api] Fetching parts metadata...');

    const token = sessionStorage.getItem('fastenai_access_token');
    if (!token) {
      throw new Error('No access token available');
    }

    // Cache-busting timestamp
    const timestamp = Date.now();
    const url = `${APP_URL}/api/get-parts-metadata?documentId=${documentId}&workspaceId=${workspaceId}&elementId=${elementId}&_=${timestamp}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        // Cache-busting headers
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0',
      },
      credentials: 'include',
    });

    if (response.status === 401) {
      if (retryCount < 1) {
        console.log('[onshape-api] Token expired, refreshing...');
        await refreshAccessToken();
        return getPartsMetadata(documentId, workspaceId, elementId, retryCount + 1);
      } else {
        console.error('[onshape-api] Token refresh failed after retry');
        throw new Error('Unauthorized: Please sign in again');
      }
    }

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[onshape-api] getPartsMetadata failed:', response.status, errorText);
      throw new Error(`Failed to fetch parts metadata: ${response.status} ${errorText}`);
    }

    const result = await response.json();

    if (!result.success) {
      console.error('[onshape-api] getPartsMetadata failed:', result.error);
      throw new Error(result.error || 'Failed to fetch parts metadata');
    }

    return result.parts;
  } catch (error) {
    // If it's already a handled 401 error, re-throw it
    if (error.message && error.message.includes('Unauthorized')) {
      throw error;
    }

    console.error('Error fetching parts metadata:', error);
    throw error;
  }
}

/**
 * Updates a single property (name, description, part number, vendor, etc.) for a specific part.
 * @param {string} documentId - Onshape document ID
 * @param {string} workspaceId - Onshape workspace ID
 * @param {string} elementId - Onshape element ID
 * @param {string} partId - Part ID to update
 * @param {string} property - Property to update (name, description, partNumber, vendor, project, revision)
 * @param {string} newValue - New value for the property
 * @param {number} retryCount - Number of retries attempted (for recursion)
 * @returns {Promise<Object>} Result object with success status
 */
export async function updatePartMetadata(documentId, workspaceId, elementId, partId, property, newValue, retryCount = 0) {
  try {
    console.log(`[onshape-api] Updating part metadata: ${property} = "${newValue}"`);

    const token = sessionStorage.getItem('fastenai_access_token');
    if (!token) {
      throw new Error('No access token available');
    }

    const url = `${APP_URL}/api/update-part-metadata`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        // Cache-busting headers
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
      },
      credentials: 'include',
      body: JSON.stringify({
        documentId,
        workspaceId,
        elementId,
        partId,
        property,
        newValue,
      }),
    });

    if (response.status === 401) {
      if (retryCount < 1) {
        console.log('[onshape-api] Token expired, refreshing...');
        await refreshAccessToken();
        return updatePartMetadata(documentId, workspaceId, elementId, partId, property, newValue, retryCount + 1);
      } else {
        console.error('[onshape-api] Token refresh failed after retry');
        throw new Error('Unauthorized: Please sign in again');
      }
    }

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[onshape-api] updatePartMetadata failed:', response.status, errorText);
      return {
        success: false,
        error: `Failed to update part metadata: ${response.status} ${errorText}`,
      };
    }

    const result = await response.json();

    if (!result.success) {
      console.error('[onshape-api] updatePartMetadata failed:', result.error);
      return {
        success: false,
        error: result.error || 'Failed to update part metadata',
      };
    }

    console.log('[onshape-api] Part metadata updated successfully');
    return result;

  } catch (error) {
    console.error('Error updating part metadata:', error);
    return {
      success: false,
      error: error.message || 'An error occurred while updating part metadata',
    };
  }
}

/**
 * Maps Onshape internal Feature Types to readable names.
 * ID Reference:
 * 151 = BTMSketch (Sketch)
 * 134 = BTMFeature (Standard Features like Extrude, Fillet, etc.)
 * 148 = BTMAssemblyFeature (Mates, Groups)
 * 
 * @param {Object} feature - The Onshape feature object
 * @returns {string} Human-readable feature type name
 */
export function getReadableFeatureType(feature) {
  if (!feature) return 'Invalid Feature';

  // 1. SAFELY EXTRACT IDENTIFIERS (Look everywhere)
  // Onshape API returns: type (numeric), typeName (string), or legacy btType/featureType
  const btType = feature.btType || feature.message?.btType || feature.typeName;
  const typeId = feature.featureType || feature.message?.featureType || feature.type;
  
  // 2. SKETCHES (ID 151)
  // Check string "BTMSketch" or ID 151
  if ((typeof btType === 'string' && btType.includes('BTMSketch')) || typeId === 151) {
    return 'Sketch';
  }

  // 3. STANDARD FEATURES (ID 134)
  // Check string "BTMFeature" or ID 134
  if ((typeof btType === 'string' && btType.includes('BTMFeature')) || typeId === 134) {
    
    // The specific tool name (e.g., "extrude", "fillet")
    // It is almost always in 'message.featureType' or top-level 'featureType' string
    const specificType = feature.message?.featureType || feature.featureType;
    
    if (typeof specificType === 'string') {
      switch (specificType) {
        case 'extrude': return 'Extrude';
        case 'revolve': return 'Revolve';
        case 'fillet': return 'Fillet';
        case 'chamfer': return 'Chamfer';
        case 'boolean': return 'Boolean';
        case 'shell': return 'Shell';
        case 'hole': return 'Hole';
        case 'rib': return 'Rib';
        case 'loft': return 'Loft';
        case 'sweep': return 'Sweep';
        
        // Construction
        case 'cPlane': return 'Plane';
        case 'mateConnector': return 'Mate Connector';
        
        // Patterns
        case 'patternLinear': return 'Linear Pattern';
        case 'patternCircular': return 'Circular Pattern';
        case 'patternCurve': return 'Curve Pattern';
        case 'mirror': return 'Mirror';
        
        // Imports
        case 'importEntry': return 'Imported Part';
        case 'flatten': return 'Sheet Metal Flat Pattern';
        
        default:
          // Clean up unknown strings (e.g. "thick" -> "Thick")
          return specificType.charAt(0).toUpperCase() + specificType.slice(1);
      }
    }
  }

  // 4. ASSEMBLIES (ID 148)
  if ((typeof btType === 'string' && btType.includes('BTMAssemblyFeature')) || typeId === 148) {
    return 'Assembly Feature';
  }

  // 5. DEBUGGING FALLBACK
  // If we still fail, return the keys we DID find so we can fix it
  return `Unknown (${JSON.stringify(Object.keys(feature))})`;
}

/**
 * Get selection details (geometry analysis) from Onshape API via backend using FeatureScript
 * Automatically handles token expiration by refreshing or re-authenticating
 * @param {string} documentId - Onshape document ID
 * @param {string} workspaceId - Onshape workspace ID
 * @param {string} elementId - Onshape element ID (part studio)
 * @param {Array<string>} selectionIds - Array of transient IDs to analyze
 * @param {number} retryCount - Internal retry counter (default: 0)
 * @returns {Promise<Array>} Response with selection analysis array
 */
export async function getSelectionDetails(documentId, workspaceId, elementId, selectionIds, retryCount = 0) {
  // Validate required parameters
  if (!documentId || !workspaceId || !elementId) {
    throw new Error('Missing required parameters: documentId, workspaceId, elementId');
  }

  if (!Array.isArray(selectionIds) || selectionIds.length === 0) {
    throw new Error('Missing or invalid selectionIds parameter. Expected non-empty array.');
  }

  // Prevent infinite retry loops
  const MAX_RETRIES = 1;
  if (retryCount > MAX_RETRIES) {
    throw new Error('Maximum retry attempts reached. Please try again later.');
  }

  // Build query string
  const params = new URLSearchParams({
    documentId,
    workspaceId,
    elementId,
    selectionIds: JSON.stringify(selectionIds), // Send as JSON string
  });

  const url = `${APP_URL}/api/get-selection-details?${params.toString()}`;

  // Get access token from storage for Authorization header
  let accessToken = getStoredAccessToken();
  const headers = {
    'Content-Type': 'application/json',
  };
  
  // Add Authorization header if token is available
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers,
      credentials: 'include', // Still include cookies as fallback
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[onshape-api] getSelectionDetails error response:', {
        status: response.status,
        statusText: response.statusText,
        errorText: errorText.substring(0, 500), // First 500 chars
      });
      
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch (e) {
        errorData = { error: `HTTP error! status: ${response.status}`, rawText: errorText };
      }
      
      // Handle 401 Unauthorized - token expired
      if (response.status === 401) {
        // Only retry if we haven't already retried
        if (retryCount === 0) {
          try {
            // Attempt to refresh/re-authenticate
            const newAccessToken = await handleTokenExpiration();
            
            // Retry the request with new token
            return getSelectionDetails(documentId, workspaceId, elementId, selectionIds, retryCount + 1);
          } catch (refreshError) {
            // If refresh/re-auth failed, throw the original 401 error with helpful message
            throw new Error('Unauthorized: Invalid or expired access token. Please sign in again.');
          }
        } else {
          // Already retried once, throw error
          throw new Error('Unauthorized: Invalid or expired access token. Please sign in again.');
        }
      }
      
      // For other errors, throw normally
      throw new Error(errorData.error || `Failed to fetch selection details: ${response.status}`);
    }

    const result = await response.json();

    if (!result.success) {
      console.error('[onshape-api] getSelectionDetails failed:', result.error);
      throw new Error(result.error || 'Failed to fetch selection details');
    }

    return result.analysis;
  } catch (error) {
    // If it's already a handled 401 error, re-throw it
    if (error.message && error.message.includes('Unauthorized')) {
      throw error;
    }
    
    console.error('Error fetching selection details:', error);
    throw error;
  }
}

/**
 * Update feature parameter in Onshape Part Studio via backend
 * Automatically handles token expiration by refreshing or re-authenticating
 * @param {string} documentId - Onshape document ID
 * @param {string} workspaceId - Onshape workspace ID
 * @param {string} elementId - Onshape element ID (part studio)
 * @param {string} featureName - Name of the feature to update (e.g., "Extrude 1", "Base Extrude")
 * @param {string} parameterId - Parameter ID to update (e.g., "depth", "radius", "holeSize")
 * @param {string} newValue - New value with units (e.g., "50 mm", "0.25 in")
 * @param {number} retryCount - Internal retry counter (default: 0)
 * @returns {Promise<Object>} Response with success status and message
 */
export async function updateFeatureParameter(documentId, workspaceId, elementId, featureName, parameterId, newValue, retryCount = 0) {
  // Validate required parameters
  if (!documentId || !workspaceId || !elementId) {
    throw new Error('Missing required parameters: documentId, workspaceId, elementId');
  }

  if (!featureName || !parameterId || !newValue) {
    throw new Error('Missing required parameters: featureName, parameterId, newValue');
  }

  // Prevent infinite retry loops
  const MAX_RETRIES = 1;
  if (retryCount > MAX_RETRIES) {
    throw new Error('Maximum retry attempts reached. Please try again later.');
  }

  const url = `${APP_URL}/api/update-feature-parameter`;

  // Get access token from storage for Authorization header
  let accessToken = getStoredAccessToken();
  const headers = {
    'Content-Type': 'application/json',
  };
  
  // Add Authorization header if token is available
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers,
      credentials: 'include', // Still include cookies as fallback
      body: JSON.stringify({
        documentId,
        workspaceId,
        elementId,
        featureName,
        parameterId,
        newValue,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ 
        error: `HTTP error! status: ${response.status}` 
      }));
      
      // Handle 401 Unauthorized - token expired
      if (response.status === 401) {
        // Only retry if we haven't already retried
        if (retryCount === 0) {
          try {
            // Attempt to refresh/re-authenticate
            const newAccessToken = await handleTokenExpiration();
            
            // Retry the request with new token
            return updateFeatureParameter(documentId, workspaceId, elementId, featureName, parameterId, newValue, retryCount + 1);
          } catch (refreshError) {
            // If refresh/re-auth failed, throw the original 401 error with helpful message
            throw new Error('Unauthorized: Invalid or expired access token. Please sign in again.');
          }
        } else {
          // Already retried once, throw error
          throw new Error('Unauthorized: Invalid or expired access token. Please sign in again.');
        }
      }
      
      // For other errors, throw normally
      throw new Error(errorData.error || `Failed to update feature parameter: ${response.status}`);
    }

    const result = await response.json();

    if (!result.success) {
      throw new Error(result.error || 'Failed to update feature parameter');
    }

    return result;
  } catch (error) {
    // If it's already a handled 401 error, re-throw it
    if (error.message && error.message.includes('Unauthorized')) {
      throw error;
    }
    
    console.error('Error updating feature parameter:', error);
    throw error;
  }
}

/**
 * Maps user-friendly parameter names to Onshape parameter IDs
 * @param {string} userParameterId - User-provided parameter name (e.g., "length", "height")
 * @param {string} featureType - Readable feature type (e.g., "Extrude", "Fillet", "Hole")
 * @returns {string} Mapped Onshape parameter ID
 */
function mapParameterName(userParameterId, featureType) {
  if (!userParameterId || typeof userParameterId !== 'string') {
    return userParameterId;
  }

  const normalizedParam = userParameterId.toLowerCase().trim();

  // Map based on feature type
  switch (featureType) {
    case 'Extrude':
      // Map common synonyms for extrude depth
      if (['length', 'height', 'thickness', 'extrusion', 'extrude'].includes(normalizedParam)) {
        return 'depth';
      }
      // Keep other parameters as-is (e.g., "endBound", "oppositeDirection")
      return userParameterId;

    case 'Fillet':
      // Map common synonyms for fillet radius
      if (['width', 'size', 'fillet'].includes(normalizedParam)) {
        return 'radius';
      }
      return userParameterId;

    case 'Hole':
      // Map common synonyms for hole size
      // Note: 'holeSize' is a neutral placeholder - the backend determines the actual
      // parameter (isoHoleTable, ansiHoleTable, holeDiameter) based on the target standard
      if (['size', 'diameter', 'hole'].includes(normalizedParam)) {
        return 'holeSize';
      }
      // Map depth synonyms
      if (['length', 'height', 'depth'].includes(normalizedParam)) {
        return 'holeDepth';
      }
      // Pass through V2 hole table parameters directly (they're valid Onshape parameter IDs)
      // These should be used when the target standard is explicitly known
      if (['isoholetable', 'isoholetableex', 'ansiholetable', 'ansiholetableex', 'unitssystem'].includes(normalizedParam)) {
        // Return the properly cased version
        const properCase = {
          'isoholetable': 'isoHoleTable',
          'isoholetableex': 'isoHoleTableEx',
          'ansiholetable': 'ansiHoleTable',
          'ansiholetableex': 'ansiHoleTableEx',
          'unitssystem': 'unitsSystem'
        };
        return properCase[normalizedParam] || userParameterId;
      }
      return userParameterId;

    default:
      // For unknown feature types, return as-is (allows direct Onshape parameter IDs)
      return userParameterId;
  }
}

/**
 * Update feature parameter in Onshape Part Studio via Backend Proxy
 * Automatically handles token expiration by refreshing or re-authenticating
 * @param {string} documentId - Onshape document ID
 * @param {string} workspaceId - Onshape workspace ID
 * @param {string} elementId - Onshape element ID (part studio)
 * @param {Object} args - Tool call arguments { featureName, parameterId, newValue }
 * @param {number} retryCount - Internal retry counter (default: 0)
 * @returns {Promise<Object>} Response with success status and message
 */
export async function updateFeatureInOnshape(documentId, workspaceId, elementId, args, retryCount = 0) {
  const { featureName, parameterId, newValue } = args;

  // Validate required parameters
  if (!documentId || !workspaceId || !elementId) {
    throw new Error('Missing required parameters: documentId, workspaceId, elementId');
  }

  if (!featureName || !parameterId || !newValue) {
    throw new Error('Missing required parameters: featureName, parameterId, newValue');
  }

  // Prevent infinite retry loops
  const MAX_RETRIES = 1;
  if (retryCount > MAX_RETRIES) {
    throw new Error('Maximum retry attempts reached. Please try again later.');
  }

  // Get access token from storage for Authorization header
  let accessToken = getStoredAccessToken();
  const url = `${APP_URL}/api/update-feature-parameter`;
  
  console.log('[updateFeatureInOnshape] Proxying update request to:', url);

  const headers = {
    'Content-Type': 'application/json',
  };
  
  // Add Authorization header if token is available
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers,
      credentials: 'include', // Still include cookies as fallback
      body: JSON.stringify({
        documentId,
        workspaceId,
        elementId,
        featureName,
        parameterId,
        newValue,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ 
        error: `HTTP error! status: ${response.status}` 
      }));
      
      // Handle 401 Unauthorized - token expired
      if (response.status === 401) {
        // Only retry if we haven't already retried
        if (retryCount === 0) {
          try {
            // Attempt to refresh/re-authenticate
            const newAccessToken = await handleTokenExpiration();
            
            // Retry the request with new token
            return updateFeatureInOnshape(documentId, workspaceId, elementId, args, retryCount + 1);
          } catch (refreshError) {
            // If refresh/re-auth failed, throw the original 401 error with helpful message
            throw new Error('Unauthorized: Invalid or expired access token. Please sign in again.');
          }
        } else {
          // Already retried once, throw error
          throw new Error('Unauthorized: Invalid or expired access token. Please sign in again.');
        }
      }

      // For other errors, log full error response including diagnostics
      console.error('========================================');
      console.error('[ERROR RESPONSE - START]');
      console.error('[updateFeatureInOnshape] Full error response:', errorData);
      console.error('[updateFeatureInOnshape] Has diagnostics?', !!errorData.diagnostics);
      console.error('[updateFeatureInOnshape] Response keys:', Object.keys(errorData));
      console.error('========================================');
      if (errorData.diagnostics) {
        console.error('[DIAGNOSTICS]', errorData.diagnostics);
        console.error('[DIAGNOSTICS] Detected Mode:', errorData.diagnostics.detectedMode);
        console.error('[DIAGNOSTICS] Scrubbed Keys:', errorData.diagnostics.scrubbedKeys);
        console.error('[DIAGNOSTICS] Reset Keys:', errorData.diagnostics.resetKeys);
        console.error('[DIAGNOSTICS] Hole Tables Before Cleanup:', errorData.diagnostics.holeTablesBeforeCleanup);
        console.error('[DIAGNOSTICS] Hole Tables After Cleanup:', errorData.diagnostics.holeTablesAfterCleanup);
        console.error('[DIAGNOSTICS] Cross Standard Cleanup:', errorData.diagnostics.crossStandardCleanup);
        console.error('[DIAGNOSTICS] Final Payload:', errorData.diagnostics.finalPayload);
      }
      throw new Error(errorData.error || `Failed to update feature parameter: ${response.status}`);
    }

    const result = await response.json();

    if (!result.success) {
      // Log diagnostics even on soft failures
      if (result.diagnostics) {
        console.log('[SUCCESS WITH DIAGNOSTICS]', result.diagnostics);
      }
      throw new Error(result.error || 'Failed to update feature parameter');
    }

    // Log diagnostics on success too
    if (result.diagnostics) {
      console.log('[SUCCESS DIAGNOSTICS]', result.diagnostics);
    }

    return result;
  } catch (error) {
    // If it's already a handled 401 error, re-throw it
    if (error.message && error.message.includes('Unauthorized')) {
      throw error;
    }

    console.error('[updateFeatureInOnshape] Error updating feature:', error);
    throw error;
  }
}

/**
 * Get the element type (PARTSTUDIO, ASSEMBLY, DRAWING) for an Onshape element.
 */
export async function getElementType(documentId, workspaceId, elementId) {
  const token = sessionStorage.getItem('fastenai_access_token');
  if (!token) throw new Error('No access token available');

  const params = new URLSearchParams({ documentId, workspaceId, elementId });
  const url = `${APP_URL}/api/get-element-type?${params.toString()}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
    credentials: 'include',
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
    throw new Error(err.error || `getElementType failed: ${response.status}`);
  }

  return response.json(); // { success, elementType }
}

/**
 * Export a drawing as DRAWING_JSON and return a clean LLM-ready context.
 * Includes views, dimensions, GD&T frames, centermarks, and notes.
 * NOTE: This triggers a translation and can take 10–30 seconds.
 */
export async function getDrawingContext(documentId, workspaceId, elementId) {
  let token = await getDrawingToken();

  const params = new URLSearchParams({ documentId, workspaceId, elementId });
  const url = `${APP_URL}/api/get-drawing-context?${params.toString()}`;

  let response = await fetch(url, {
    method: 'GET',
    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
    credentials: 'include',
  });

  if (response.status === 401) {
    token = await handleTokenExpiration();
    response = await fetch(url, {
      method: 'GET',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      credentials: 'include',
    });
  }

  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
    throw new Error(err.error || `getDrawingContext failed: ${response.status}`);
  }

  return response.json(); // { success, views, dimensions, geometricTolerances, centermarks, notes }
}

/**
 * Get a valid access token, refreshing if necessary.
 */
async function getDrawingToken() {
  let token = sessionStorage.getItem('fastenai_access_token');
  if (!token) {
    token = await handleTokenExpiration();
  }
  return token;
}

/**
 * POST to a drawing API endpoint with automatic 401 retry after token refresh.
 */
async function drawingPost(path, body) {
  let token = await getDrawingToken();
  let response = await fetch(`${APP_URL}${path}`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  });

  if (response.status === 401) {
    // Token expired — refresh and retry once
    token = await handleTokenExpiration();
    response = await fetch(`${APP_URL}${path}`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(body),
    });
  }

  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
    const detail = err.details ? ` | ${err.details}` : '';
    throw new Error((err.error || `${path} failed: ${response.status}`) + detail);
  }
  return response.json();
}

/**
 * Create drawing views. viewTypes is an optional array: ["front","top","right","iso"].
 * Defaults to all 4 if omitted.
 * Optional: sheetSize ("A"|"B"|"C"|"D"), viewPositions ({ front: {x,y}, ... }), centerX, centerY.
 */
export async function createDrawingViews(documentId, workspaceId, drawingElementId, partStudioElementId, partId = null, viewTypes = null, options = {}) {
  const { sheetSize, viewPositions, centerX, centerY } = options;
  return drawingPost('/api/create-drawing-views', {
    documentId,
    workspaceId,
    drawingElementId,
    partStudioElementId,
    ...(partId ? { partId } : {}),
    ...(viewTypes ? { viewTypes } : {}),
    ...(sheetSize ? { sheetSize } : {}),
    ...(viewPositions ? { viewPositions } : {}),
    ...(typeof centerX === 'number' ? { centerX } : {}),
    ...(typeof centerY === 'number' ? { centerY } : {}),
  });
}

/**
 * Add stacked PointToPoint linear dimensions to all hole centers in a view.
 */
export async function addLinearDimensions(documentId, workspaceId, drawingElementId, viewId, cachedDrawingContext = null) {
  return drawingPost('/api/add-linear-dimensions', { documentId, workspaceId, drawingElementId, viewId, ...(cachedDrawingContext ? { cachedDrawingContext } : {}) });
}

/**
 * Add ordinate (baseline) dimensions originating from auto-picked datum edges.
 * Pass viewId = null to dimension every eligible view.
 */
export async function addOrdinateDimensions(documentId, workspaceId, drawingElementId, viewId = null, cachedDrawingContext = null) {
  return drawingPost('/api/add-ordinate-dimensions', { documentId, workspaceId, drawingElementId, viewId, ...(cachedDrawingContext ? { cachedDrawingContext } : {}) });
}

/**
 * Modify a dimension's type (basic, reference, or driven).
 * modification: "basic" | "reference" | "driven"
 */
export async function modifyDimension(documentId, workspaceId, drawingElementId, dimensionLogicalId, modification) {
  return drawingPost('/api/modify-dimension', { documentId, workspaceId, drawingElementId, dimensionLogicalId, modification });
}

/**
 * Modify ALL dimensions on a drawing to the same type (basic, reference, or driven).
 * modification: "basic" | "reference" | "driven"
 */
export async function modifyAllDimensions(documentId, workspaceId, drawingElementId, modification) {
  return drawingPost('/api/modify-all-dimensions', { documentId, workspaceId, drawingElementId, modification });
}

/**
 * One-shot pipeline: create views, detect hole circles, auto-add centermarks, add linear dimensions.
 * viewTypes: optional array of "front", "top", "right", "iso" (defaults to all 4).
 * Optional: sheetSize ("A"|"B"|"C"|"D"), viewPositions ({ front: {x,y}, ... }), centerX, centerY.
 */
export async function createLinearViews(documentId, workspaceId, drawingElementId, partStudioElementId, partId = null, viewTypes = null, options = {}) {
  const { sheetSize, viewPositions, centerX, centerY } = options;
  return drawingPost('/api/create-linear-views', {
    documentId,
    workspaceId,
    drawingElementId,
    partStudioElementId,
    ...(partId ? { partId } : {}),
    ...(viewTypes ? { viewTypes } : {}),
    ...(sheetSize ? { sheetSize } : {}),
    ...(viewPositions ? { viewPositions } : {}),
    ...(typeof centerX === 'number' ? { centerX } : {}),
    ...(typeof centerY === 'number' ? { centerY } : {}),
  });
}

/**
 * Add Onshape::HoleCallout annotations to holes in a drawing view.
 * Onshape auto-generates callout text from the model (diameter, thread spec, depth, etc.).
 * Identical-sized holes are grouped with an NX prefix.
 * viewId is optional — if omitted, callouts are added to all views with hole data.
 */
export async function addHoleCallouts(documentId, workspaceId, drawingElementId, viewId = null, cachedDrawingContext = null) {
  return drawingPost('/api/add-hole-callouts', {
    documentId,
    workspaceId,
    drawingElementId,
    ...(viewId ? { viewId } : {}),
    ...(cachedDrawingContext ? { cachedDrawingContext } : {}),
  });
}

/**
 * Add Onshape::Datum annotations to a drawing.
 * datums: array of { contents, viewId?, centermarkId?, position?: {x,y}, targetPosition?: {x,y,uniqueId?,snapPointType?} }
 * contents must be a single uppercase letter A-Z.
 */
export async function addDatums(documentId, workspaceId, drawingElementId, datums) {
  return drawingPost('/api/add-datums', { documentId, workspaceId, drawingElementId, datums });
}

/**
 * Add a GD&T geometric tolerance frame to a drawing.
 * frames: array of { characteristic, tolerance, modifier?, datums?, diameter? }
 * position: optional { x, y } paper-space inches (defaults to [6, 6] on the sheet)
 */
export async function addGdtFrame(documentId, workspaceId, drawingElementId, frames, position = null) {
  return drawingPost('/api/add-gdt-frame', {
    documentId, workspaceId, drawingElementId, frames,
    ...(position ? { position } : {}),
  });
}

/**
 * TEST/DIAGNOSTIC: Export a drawing element as DRAWING_JSON and return the raw result.
 * Used to verify what annotation data Onshape exposes via the translations API.
 */
export async function testJsongeometry(documentId, workspaceId, elementId) {
  const token = sessionStorage.getItem('fastenai_access_token');
  if (!token) throw new Error('No access token available');

  const params = new URLSearchParams({ documentId, workspaceId, elementId });
  const url = `${APP_URL}/api/test-jsongeometry?${params.toString()}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
    throw new Error(err.error || `jsongeometry test failed: ${response.status}`);
  }

  return response.json();
}

export async function getDrawingJson(documentId, workspaceId, elementId) {
  const token = sessionStorage.getItem('fastenai_access_token');
  if (!token) throw new Error('No access token available');

  const params = new URLSearchParams({ documentId, workspaceId, elementId });
  const url = `${APP_URL}/api/get-drawing-json?${params.toString()}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
    throw new Error(err.error || `Drawing JSON fetch failed: ${response.status}`);
  }

  return response.json();
}

/**
 * Execute an ordered sequence of sketch/extrude steps against the active Part Studio.
 * Steps are executed serially; face geometry IDs are resolved automatically between steps.
 *
 * @param {string} documentId
 * @param {string} workspaceId
 * @param {string} elementId
 * @param {Array} steps - Array of step objects:
 *   { type: "sketch", plane: "TOP"|{source:"face_of_last_extrude",normalHint:[x,y,z]},
 *     entities: [{kind:"circle",cx,cy,diameter},...] }
 *   { type: "extrude", depth: "20 mm" }
 * @param {Object} [options]
 * @param {boolean} [options.dryRun] - Return payloads without calling Onshape.
 */
export async function createFeatureSequence(documentId, workspaceId, elementId, steps, options = {}) {
  const token = sessionStorage.getItem('fastenai_access_token');
  if (!token) throw new Error('No access token available');

  const response = await fetch(`${APP_URL}/api/create-feature-sequence`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({ documentId, workspaceId, elementId, steps, dryRun: !!options.dryRun }),
  });

  const body = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
  if (!response.ok) {
    const err = new Error(body.error || `createFeatureSequence failed: ${response.status}`);
    err.steps = body.steps;
    err.onshapeBody = body.onshapeBody;
    throw err;
  }
  return body;
}

/**
 * Dev smoke test: create a 10mm Ø × 20mm cylinder in the active Part Studio.
 * Returns the full step trace (plane resolve → sketch POST → extrude POST)
 * so the caller can render the result for fast iteration on payload shape.
 */
export async function getRawSketchFeatures(documentId, workspaceId, elementId) {
  const token = sessionStorage.getItem('fastenai_access_token');
  if (!token) throw new Error('No access token available');

  const params = new URLSearchParams({ documentId, workspaceId, elementId });
  const response = await fetch(`${APP_URL}/api/get-raw-sketch-features?${params}`, {
    method: 'GET',
    headers: { 'Authorization': `Bearer ${token}` },
    credentials: 'include',
  });

  const body = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
  if (!response.ok) throw new Error(body.error || `getRawSketchFeatures failed: ${response.status}`);
  return body;
}

export async function createCylinderTest(documentId, workspaceId, elementId, options = {}) {
  const token = sessionStorage.getItem('fastenai_access_token');
  if (!token) throw new Error('No access token available');

  const response = await fetch(`${APP_URL}/api/create-cylinder-test`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({
      documentId,
      workspaceId,
      elementId,
      diameter: options.diameter || '10 mm',
      length: options.length || '20 mm',
      plane: options.plane || 'TOP',
      dryRun: !!options.dryRun,
    }),
  });

  const body = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
  if (!response.ok) {
    const err = new Error(body.error || `createCylinderTest failed: ${response.status}`);
    err.steps = body.steps;
    err.fsResponse = body.fsResponse;
    err.fsNotices = body.fsNotices;
    err.onshapeError = body.onshapeError;
    err.sketchPayload = body.sketchPayload;
    throw err;
  }
  return body;
}

/**
 * POST a JSON body to a hardware endpoint with the bearer token, refreshing once on 401.
 */
async function postHardware(path, body, retryCount = 0) {
  const token = getStoredAccessToken();
  const response = await fetch(`${APP_URL}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    },
    credentials: 'include',
    body: JSON.stringify(body),
  });

  if (response.status === 401 && retryCount < 1) {
    await handleTokenExpiration();
    return postHardware(path, body, retryCount + 1);
  }

  const result = await response.json().catch(() => ({}));
  if (!response.ok || result.success === false) {
    throw new Error(result.error || `Request failed: ${response.status}`);
  }
  return result;
}

/**
 * Read a hardware datasheet PDF with Gemini. Returns editable fields; mutates nothing.
 * @param {string} pdfBase64
 * @param {string} [cadFilename] - used as a size hint when the datasheet covers a range
 */
export async function analyzeHardwareDatasheet(pdfBase64, cadFilename) {
  return postHardware('/api/hardware/analyze', { pdfBase64, cadFilename });
}

/**
 * The user's default color per material (Settings), plus the Onshape Material
 * Library names. Pass materialColors ([{material, color}]) to save them first.
 */
export async function hardwareMaterialColors(materialColors) {
  return postHardware('/api/hardware/library', { action: 'materialColors', materialColors });
}

/**
 * START a hardware import: uploads the CAD (Onshape translates it into a Part
 * Studio tab) and the datasheet PDF. Returns { translationId } immediately.
 */
export async function startHardwareImport({
  documentId, workspaceId, cadBase64, cadFilename, pdfBase64, pdfFilename, fields, documentName,
}) {
  return postHardware('/api/hardware/import', {
    documentId, workspaceId, cadBase64, cadFilename, pdfBase64, pdfFilename, fields, documentName,
  });
}

/**
 * FINISH a hardware import. Returns { state: 'ACTIVE' } while Onshape is still
 * translating; once DONE, applies the confirmed fields and returns per-part results.
 */
export async function finishHardwareImport({ documentId, workspaceId, translationId, fields }) {
  return postHardware('/api/hardware/import', { documentId, workspaceId, translationId, fields });
}

/**
 * Element type of the tab Otto is open in (PARTSTUDIO / ASSEMBLY / DRAWING …).
 * Decides between the hardware tool belt (assemblies) and Add Hardware (everything else).
 */
export async function getHardwareContext({ documentId, workspaceId, elementId }) {
  return postHardware('/api/hardware/library', { action: 'context', documentId, workspaceId, elementId });
}

/**
 * The user's hardware library. Pass folderId (id or Onshape folder link) to choose
 * a new library folder; omit it to use the saved one. Returns { needsFolder,
 * candidateFolders } when none is set yet, else { folder, documents, favorites, usage }.
 */
export async function listHardwareLibrary(folderId, { refresh = false } = {}) {
  return postHardware('/api/hardware/library', {
    action: 'list', ...(folderId ? { folderId } : {}), ...(refresh ? { refresh: true } : {}),
  });
}

/**
 * One level of the user's Onshape drive for the library folder picker:
 * { folders: [{id, name}], documentCount }. No folderId = "My Onshape".
 */
export async function browseHardwareFolders(folderId) {
  return postHardware('/api/hardware/library', { action: 'browse', ...(folderId ? { folderId } : {}) });
}

export async function setHardwareFavorite(documentId, favorite) {
  return postHardware('/api/hardware/library', { action: 'favorite', documentId, favorite });
}

/**
 * Insert a library document's part into the assembly Otto is open in. May return
 * { needsVersion, versionName } or { needsPartChoice, parts } instead of inserting;
 * call again with createVersion: true / partIds.
 */
export async function insertLibraryHardware({
  documentId, workspaceId, elementId, sourceDocumentId, sourceWorkspaceId, sourceModifiedAt,
  quantity, partIds, createVersion,
}) {
  // sourceWorkspaceId / sourceModifiedAt come from the library listing and save
  // the backend Onshape calls (see the API budget in hardwareController.js).
  return postHardware('/api/hardware/insert', {
    documentId, workspaceId, elementId, sourceDocumentId, sourceWorkspaceId, sourceModifiedAt,
    quantity, partIds, createVersion,
  });
}
